import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { RefreshCw, X } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { logger } from '@/shared/utils/logger';
import { useAuthStateSafe } from '@/core/auth/useAuthStateSafe';

// Route pubbliche dove il modale aggiornamento NON deve apparire
const PUBLIC_ROUTES = ['/auth', '/device'];
interface ServiceWorkerUpdateNotificationProps {
  className?: string;
}

interface NewVersionInfo {
  appVersion: string;
  releaseNotes: string;
}

// Allinea localStorage['app-version'] alla versione corrente di version.json.
// Serve a evitare che il version-check (trigger periodico) faccia riapparire il
// banner dopo un reload quando NON esiste un service worker in stato "waiting".
const syncStoredVersion = async (): Promise<void> => {
  try {
    const response = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
    const data = await response.json();
    if (data?.version) {
      localStorage.setItem('app-version', data.version);
    }
  } catch (error) {
    logger.debug('Failed to sync stored version before reload:', error);
  }
};

const ServiceWorkerUpdateNotification: React.FC<ServiceWorkerUpdateNotificationProps> = ({ className }) => {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [newVersionInfo, setNewVersionInfo] = useState<NewVersionInfo | null>(null);
  const { user } = useAuthStateSafe();
  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTES.some(r => location.pathname.startsWith(r));

  // Check versione attivo - confronta con version.json
  useEffect(() => {
    if (!user || isPublicRoute) return;
    const checkVersion = async () => {
      try {
        // Fetch con cache-busting per avere sempre la versione fresca
        const response = await fetch('/version.json?t=' + Date.now(), {
          cache: 'no-store'
        });
        const data = await response.json();

        // Confronta con la versione salvata
        const storedVersion = localStorage.getItem('app-version');

        // Prima esecuzione assoluta (nessuna versione salvata): registra la
        // versione corrente come baseline e NON mostrare il banner.
        if (!storedVersion) {
          localStorage.setItem('app-version', data.version);
          return;
        }

        if (storedVersion !== data.version) {
          // C'è una nuova versione disponibile.
          logger.debug(`Version mismatch: stored=${storedVersion}, current=${data.version}`);
          // NB: NON cancelliamo qui 'sw-update-dismissed'. Cancellarlo a ogni tick
          // del polling rendeva il banner non-chiudibile durante il loop (un client
          // incastrato vedeva la X/«PIÙ TARDI» annullati 60s dopo). Il dismiss vale
          // per la sessione; se l'utente lo chiude, resta chiuso fino a un reload.
          if (sessionStorage.getItem('sw-update-dismissed') !== 'true') {
            setNewVersionInfo({
              appVersion: data.appVersion || '',
              releaseNotes: data.releaseNotes || '',
            });
            setShowUpdateBanner(true);
          }
          // NON sovrascriviamo 'app-version' qui: la versione salvata viene
          // allineata SOLO quando l'utente applica davvero l'update (handleUpdate)
          // o al primo avvio del client già aggiornato. Sovrascrivere subito
          // nascondeva il fatto che l'update non era ancora stato applicato.
        }
      } catch (error) {
        // Ignora errori di rete
        logger.debug('Failed to check version:', error);
      }
    };

    // Check immediato
    checkVersion();

    // Check periodico ogni 60 secondi
    const versionCheckInterval = setInterval(checkVersion, 60000);

    return () => clearInterval(versionCheckInterval);
  }, [user, isPublicRoute]);

  useEffect(() => {
    if (!user || isPublicRoute) return;
    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      return;
    }

    // Mostra il banner rispettando il dismiss di sessione (non lo cancella mai
    // automaticamente: solo handleUpdate/handleDismiss governano quel flag).
    const showBannerRespectingDismiss = () => {
      if (sessionStorage.getItem('sw-update-dismissed') === 'true') return;
      setShowUpdateBanner(true);
    };

    // Listen for service worker updates
    const handleServiceWorkerUpdate = () => {
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);

        // Check if there's a waiting service worker
        if (reg.waiting) {
          showBannerRespectingDismiss();
        }

        // Listen for new service workers
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              showBannerRespectingDismiss();
            }
          });
        });
      });
    };

    // Listen for messages from service worker.
    // NOTA: non cancelliamo più 'sw-update-dismissed' qui. Il SW broadcasta
    // SW_UPDATED a ogni attivazione (sw.js activate handler); cancellare il
    // dismiss faceva riapparire il banner che l'utente aveva chiuso a ogni
    // ri-attivazione del SW nella stessa sessione.
    const handleMessage = (event: MessageEvent) => {
      if (sessionStorage.getItem('sw-update-dismissed') === 'true') return;

      if (event.data && event.data.type === 'SW_UPDATED') {
        logger.debug(`Service Worker updated to version: ${event.data.version}`);
        setShowUpdateBanner(true);
      }

      if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
        logger.debug(`Update available: ${event.data.newVersion}`);
        setShowUpdateBanner(true);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    handleServiceWorkerUpdate();

    // Check for updates every 30 seconds
    const interval = setInterval(() => {
      if (registration) {
        registration.update();
      }
    }, 30000);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
      clearInterval(interval);
    };
  }, [registration, user, isPublicRoute]);

  const handleUpdate = async () => {
    // Nascondi subito il banner: evita che resti visibile durante il reload
    // e che venga percepito come "non sparisce".
    setShowUpdateBanner(false);
    // Un update applicato esplicitamente annulla un eventuale dismiss precedente.
    sessionStorage.removeItem('sw-update-dismissed');

    // In OGNI ramo allineiamo la versione salvata PRIMA del reload, così il
    // version-check successivo non trova più mismatch e il banner non riappare.
    await syncStoredVersion();

    if (!registration || !registration.waiting) {
      // Nessun service worker in attesa: il banner è stato innescato dal
      // version-check (version.json) o da un SW già attivato → basta ricaricare.
      window.location.reload();
      return;
    }

    // C'è un SW in waiting: chiedigli di prendere il controllo.
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });

    // once: true previene reload duplicati
    let reloaded = false;
    const doReload = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', doReload, { once: true });

    // Fallback: se controllerchange non arriva entro 3s, ricarica comunque.
    // La versione è già stata sincronizzata sopra, quindi al reload non ci sarà
    // mismatch e il banner non riapparirà. I client con un SW vecchio incastrato
    // si auto-sbloccano: il browser controlla /sw.js ogni 60s (updateViaCache
    // 'none'), installa il nuovo SW che ora fa self.skipWaiting() in install →
    // attivazione automatica, senza bisogno di cancellare cache (NON tocchiamo
    // 'pending-operations': è la coda mutazioni offline).
    setTimeout(doReload, 3000);
  };

  const handleDismiss = () => {
    setShowUpdateBanner(false);
    // Set a flag to not show again for this session
    sessionStorage.setItem('sw-update-dismissed', 'true');
  };

  // Don't show for unauthenticated users or on public routes (login, reset password, MFA)
  if (!user || isPublicRoute) {
    return null;
  }

  // Don't show if dismissed in this session
  if (sessionStorage.getItem('sw-update-dismissed') === 'true') {
    return null;
  }

  if (!showUpdateBanner) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed bottom-[110px] left-4 right-4 md:left-auto md:right-4 md:w-auto z-[70]',
        'bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-[1.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.2)] border border-gray-100',
        'p-4 md:p-5',
        'animate-in slide-in-from-bottom-10 duration-500',
        className
      )}
    >
      <div className="flex items-start gap-4">
        <div className="p-3 bg-blue-50 rounded-2xl shadow-sm">
          <RefreshCw className="h-6 w-6 text-blue-600" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-black text-gray-900 uppercase tracking-tight mb-1 text-lg">
            Aggiornamento
          </h3>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 leading-tight">
            {newVersionInfo?.appVersion
              ? `VERSIONE ${newVersionInfo.appVersion} DISPONIBILE`
              : 'NUOVA VERSIONE DISPONIBILE'}
          </p>
          <p className="text-sm text-gray-600 mb-4 leading-relaxed">
            {newVersionInfo?.releaseNotes || 'Nuova versione pronta per l\'uso. Aggiorna per ottenere le ultime migliorie.'}
          </p>

          <div className="flex gap-3">
            <Button
              size="sm"
              onClick={handleUpdate}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black uppercase tracking-widest text-[10px] h-11 px-4 shadow-lg shadow-blue-100"
            >
              AGGIORNA ORA
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDismiss}
              className="rounded-xl font-black uppercase tracking-widest text-[10px] h-11 px-4 border-gray-100"
            >
              PIÙ TARDI
            </Button>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          aria-label="Chiudi notifica"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>
    </div>
  );
};

export default ServiceWorkerUpdateNotification;
